import * as React from 'react';
import {
    Create,
    TextInput,
    required,
    SimpleForm,
    DateInput,
    SelectInput,
    ReferenceInput,
    NullableBooleanInput,
    minValue,
    FormDataConsumer,
    ReferenceArrayInput,
    AutocompleteArrayInput,
    useDataProvider,
    DataProviderContext,
    NumberInput,
} from 'react-admin';
import HTMLEditorInput, { validHtml } from '../../common/inputs/HTMLEditorInput';
import { useParams } from 'react-router';
import { LoadingSection } from '../../common/LoadingSection';
import { STATUSES } from '../../common/constants';
import TotalAmountInput from '../../common/inputs/TotalAmountInput';
import DiscountInput from '../../common/DiscountInput';
import FileUpload from '../../common/inputs/FileUpload';
import { createCustomGoDataProvider } from '../../services';
import { DemoVideoAutoComplete } from '../../common/inputs/autoCompleteInputs/DemoVideoAutoComplee';
import { EcomProductAutoComplete } from '../../common/inputs/autoCompleteInputs/EcomProductsAutoComplete';

export const filterToQueryProductChildren = (searchText: string) =>
    searchText ? { name: `${searchText}` } : { name: `${'NOT_AVAILABLE'}` };

const ProductCreate = () => {
    let { categoryId, productTemplateId } = useParams();
    if (!productTemplateId) {
        return <LoadingSection />;
    }
    const baseDataProvider = useDataProvider();
    const customDataProvider = React.useMemo(
        () =>
            createCustomGoDataProvider(baseDataProvider, [
                { resource: 'ecom_products', apiEndpoint: '/ecom/products' },
            ]),
        [baseDataProvider],
    );

    return (
        <DataProviderContext.Provider value={customDataProvider}>
            <Create
                resource={'ecom_products'}
                redirect={(e: any, id: any) => {
                    return `ecom_categories/${categoryId}/ecom_product_templates/${productTemplateId}/ecom_products`;
                }}
                transform={(data:any) => ({
                    ...data,
                    amount: Number(data.amount),
                })}
            >
                <SimpleForm
                    defaultValues={{
                        categoryId: categoryId,
                        productTemplateId: productTemplateId,
                    }}
                >
                    <TextInput source="name" fullWidth validate={[required()]} />
                    <FileUpload
                        type="VIDEO_THUMBNAIL"
                        required={true}
                        label="select images"
                        source="images"
                        accept="image/*"
                        multiple={true}
                    />
                    <FileUpload
                        type="PDF_NOTES"
                        required={true}
                        source="previewPdf"
                        label="select preview pdf"
                        accept="application/pdf"
                        multiple={false}
                    />
                    <FileUpload
                        type="PDF_NOTES"
                        required={true}
                        source="pdfUrl"
                        label="select pdf url"
                        accept="application/pdf"
                        multiple={false}
                    />
                    <SelectInput source="status" choices={STATUSES} fullWidth />
                    <DemoVideoAutoComplete label="Demo Video" />
                    <NullableBooleanInput
                        source={'addressRequired'}
                        name={'Address Required'}
                        fullWidth
                    />
                    <NumberInput
                        type={'number'}
                        source="mrp"
                        prefix={'₹'}
                        fullWidth
                        validate={[required(), minValue(0)]}
                    />
                    <DiscountInput />
                    <NumberInput
                        type={'number'}
                        source="gstPercentage"
                        prefix={'%'}
                        fullWidth
                        validate={[required(), minValue(0)]}
                    />
                    <FormDataConsumer>
                        {({ formData, ...rest }) => {
                            return (
                                <div className="w-full">
                                    <TotalAmountInput formData={formData} source="amount" />
                                </div>
                            );
                        }}
                    </FormDataConsumer>
                    <NumberInput
                        type={'number'}
                        source="appleAmount"
                        prefix={'₹'}
                        fullWidth
                        validate={[required(), minValue(0)]}
                    />
                    <NumberInput
                        type={'number'}
                        source="heightInMtr"
                        prefix={'In Meters'}
                        fullWidth
                        validate={[required(), minValue(0)]}
                    />
                    <NumberInput
                        type={'number'}
                        source="widthInMtr"
                        prefix={'In Meters'}
                        fullWidth
                        validate={[required(), minValue(0)]}
                    />
                    <NumberInput
                        type={'number'}
                        source="lengthInMtr"
                        prefix={'In Meters'}
                        fullWidth
                        validate={[required(), minValue(0)]}
                    />
                    <NumberInput
                        type={'number'}
                        source="weightInGrams"
                        prefix={'In Grams'}
                        fullWidth
                        validate={[required(), minValue(0)]}
                    />
                    <TextInput
                        source="textDescription"
                        multiline
                        rows={4}
                        fullWidth
                        validate={[required()]}
                    />
                   <EcomProductAutoComplete label={'For Combo Products'} source={'children'} />
                    <HTMLEditorInput
                        source={'htmlDescription'}
                        fullWidth
                        validate={[required(), validHtml]}
                    />
                </SimpleForm>
            </Create>
        </DataProviderContext.Provider>
    );
};

export default ProductCreate;
